import { createApi } from "@reduxjs/toolkit/dist/query/react";
import * as logger from "common/logger";
import * as config from "config";
import * as utils from "utils";

// Helper function to get authentication headers for streaming requests
const getAuthHeaders = async (additionalHeaders = {}) => {
  const session = await utils.getSession();
  const token = session.tokens?.accessToken?.toString();
  if (!token) {
    throw new Error("Unable to retrieve auth token");
  }
  return {
    ...additionalHeaders,
    Authorization: token,
  };
};

const baseAiAgentsQuery = async (args, api, extraOptions) => {
  const payload = { ...args.payload };
  if (args.payload?.body) {
    if (!payload.method) payload.method = "POST";
    if (!payload.headers)
      payload.headers = {
        "Content-Type": "application/json",
      };
    payload.body = JSON.stringify(payload.body);
  }
  try {
    const response = await utils.authenticatedFetch(
      config.endpoints.aiAgents(args.url),
      payload
    );
    return {
      data: await response.json(),
    };
  } catch (e) {
    logger.exception(e);
    return {
      error: String(e),
    };
  }
};

// Every ai_agents endpoint answers 200 with {success: false, error} on application
// failure, so RTK Query's own error path never fires — both layers are checked here,
// once. success:false passes through as data unless `throwOnFailure` is set;
// `keepFailure` lets a caller keep an actionable failure body (ids to retry with),
// because a thrown Error flattens to a string on the way through RTK Query.
const postAiAgents = async (
  label,
  url,
  body,
  { throwOnFailure = false, keepFailure } = {}
) => {
  const response = await baseAiAgentsQuery({
    url,
    payload: body != null ? { body } : { method: "POST" },
  });
  if (response.error) throw new Error(`${label}: ${response.error}`);
  if (
    throwOnFailure &&
    response.data?.success === false &&
    !keepFailure?.(response.data)
  )
    throw new Error(`${label}: ${response.data.error || "unknown error"}`);
  return { data: response.data };
};

const baseSheetAnalysisQuery = async (args, api, extraOptions) => {
  const payload = { ...args.payload };
  if (args.payload?.body) {
    if (!payload.method) payload.method = "POST";
    if (!payload.headers)
      payload.headers = {
        "Content-Type": "application/json",
      };
    payload.body = JSON.stringify(payload.body);
  }
  try {
    const response = await utils.authenticatedFetch(
      config.endpoints.aiAgents(args.url),
      payload
    );
    return {
      data: await response.json(),
    };
  } catch (e) {
    logger.exception(e);
    return {
      error: String(e),
    };
  }
};

// Helper functions for AI Agents format
const formatColumnTypesForAiAgents = (columnTypes, dataPage, headers) => {
  const mapDataType = (typesMap) => {
    // Find the most frequent type
    let maxCount = 0;
    let primaryType = "str";

    for (const type in typesMap) {
      const count = typesMap[type].count || 0;
      if (count > maxCount) {
        maxCount = count;
        switch (type) {
          case "int":
          case "float":
            primaryType = "num";
            break;
          case "date":
          case "datetime":
            primaryType = "date";
            break;
          default:
            primaryType = "str";
        }
      }
    }
    return primaryType;
  };

  const calculateNullPercentage = (typesMap) => {
    const totalCount = Object.values(typesMap).reduce(
      (sum, typeInfo) => sum + (typeInfo.count || 0),
      0
    );
    const nullCount = typesMap.null?.count || 0;
    return totalCount > 0 ? (nullCount / totalCount) * 100 : 0;
  };

  const getUniqueCount = (typesMap) => {
    // Use the sum of all type counts as a proxy for unique values
    return Object.values(typesMap).reduce(
      (sum, typeInfo) => sum + (typeInfo.count || 0),
      0
    );
  };

  const extractSampleValues = (columnName, columnIndex) => {
    if (!dataPage || !Array.isArray(dataPage)) {
      return [];
    }

    // Extract unique values from the column
    const uniqueValues = new Set();
    const maxSamples = 5; // Limit to 5 sample values

    for (const row of dataPage) {
      if (Array.isArray(row) && columnIndex < row.length) {
        const value = row[columnIndex];
        if (value !== null && value !== undefined && value !== "") {
          uniqueValues.add(String(value).trim());
          if (uniqueValues.size >= maxSamples) {
            break;
          }
        }
      }
    }

    return Array.from(uniqueValues).slice(0, maxSamples);
  };

  return columnTypes.map((columnType) => {
    // Find the column index in headers
    const columnIndex = headers ? headers.indexOf(columnType.name) : -1;
    const sampleValues =
      columnIndex >= 0 ? extractSampleValues(columnType.name, columnIndex) : [];

    return {
      name: columnType.name,
      data_type: mapDataType(columnType.types),
      null_percentage: calculateNullPercentage(columnType.types),
      unique_count: getUniqueCount(columnType.types),
      sample_values: sampleValues,
    };
  });
};

const formatAiAgentsResponseToSuggestions = (aiAgentsResponse) => {
  const suggestions = {};

  if (aiAgentsResponse.predictions) {
    aiAgentsResponse.predictions.forEach((prediction) => {
      const sourceColumn = prediction.source_column;
      const targetColumn = prediction.target_column;
      const confidence = prediction.confidence_score;

      if (!suggestions[targetColumn]) {
        suggestions[targetColumn] = [];
      }

      suggestions[targetColumn].push({
        suggestion: sourceColumn,
        confidence: confidence,
      });
    });

    // Sort suggestions by confidence (highest first)
    Object.keys(suggestions).forEach((targetColumn) => {
      suggestions[targetColumn].sort((a, b) => b.confidence - a.confidence);
    });
  }

  return suggestions;
};

// Streaming Claims Analysis Service
export const streamingClaimsAnalysis = async (fileUrl, callbacks) => {
  try {
    const authHeaders = await getAuthHeaders({
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Accept: "text/event-stream",
    });

    const response = await fetch(
      config.endpoints.aiAgents("claims-ai-parsing-streaming"),
      {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          url: fileUrl,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Streaming failed: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.trim().startsWith("data: ")) {
          try {
            const data = JSON.parse(line.substring(6));

            // Handle different event types
            if (
              data.type === "recommended_sheet_analysis" &&
              callbacks.onRecommendedSheetAnalysis
            ) {
              const analysis = data.recommended_sheet_analysis;
              const formattedData = {
                recommended_sheet: analysis.sheet_name,
                sheets: [
                  {
                    ...analysis,
                    column_mappings: analysis.target_columns,
                  },
                ],
              };
              if (analysis.header_row) {
                formattedData.sheets[0].header_row = analysis.header_row;
              }
              callbacks.onRecommendedSheetAnalysis(formattedData);
            } else if (data.type === "complete" && callbacks.onComplete) {
              callbacks.onComplete(data.result);
            }
          } catch (parseError) {
            // Skip malformed SSE data
          }
        }
      }
    }
  } catch (error) {
    if (callbacks.onError) {
      callbacks.onError(error);
    }
    throw error;
  }
};

export const aiAgentsApi = createApi({
  reducerPath: "aiAgentsApi",
  baseQuery: baseAiAgentsQuery,
  tagTypes: [
    "IngestionData",
    "IngestionFiles",
    "TriagePipeline",
    "TriageResult",
  ],
  endpoints: (builder) => ({
    getColumnMappingSuggestionsAI: builder.query({
      queryFn: async ({ columnTypes, fileId, dataPage, headers }) => {
        // Always use AI agents approach - feature flag controlled at component level
        const formattedColumns = formatColumnTypesForAiAgents(
          columnTypes,
          dataPage,
          headers
        );
        const { data } = await postAiAgents(
          "AI Agents service failed",
          "predict-column-mappings",
          { file_name: fileId, columns: formattedColumns }
        );

        const suggestions = formatAiAgentsResponseToSuggestions(data);
        return {
          data: {
            suggestions,
            source: "ai-agents",
          },
        };
      },
    }),
    getSheetAnalysis: builder.query({
      queryFn: async (fileUrl) => {
        const payload = {
          body: {
            url: fileUrl,
          },
        };

        const response = await baseSheetAnalysisQuery({
          url: "claims-ai-parsing",
          payload: payload,
        });

        if (response.error) {
          throw new Error(`Sheet analysis failed: ${response.error}`);
        }

        return {
          data: response.data,
        };
      },
    }),
    matchClaims: builder.mutation({
      queryFn: async ({ priorClaims, currentClaims }) =>
        postAiAgents("Claims matching failed", "match-claims", {
          prior_claims: priorClaims,
          current_claims: currentClaims,
        }),
    }),
    detectDuplicates: builder.mutation({
      queryFn: async ({ claims }) =>
        postAiAgents("Duplicate detection failed", "detect-duplicates", {
          prior_claims: [],
          current_claims: claims,
          renewal: false,
        }),
    }),
    getAvailableIngestionData: builder.query({
      providesTags: ["IngestionData"],
      queryFn: async () =>
        postAiAgents(
          "Failed to get available ingestion data",
          "get-available-ingestion-data"
        ),
    }),
    agentSearchIngestionFields: builder.mutation({
      // success:false is returned, not thrown — the review dialog reads it.
      queryFn: async ({ insuredName, fields, modes, descriptions }) =>
        postAiAgents("Agent search failed", "agent-search-ingestion-fields", {
          insured_name: insuredName,
          fields,
          modes,
          descriptions,
        }),
    }),
    markFileAsUsed: builder.mutation({
      invalidatesTags: ["IngestionData"],
      queryFn: async ({ fileId, submissionId, fieldUsage }) =>
        postAiAgents("Failed to mark file as used", "mark-file-used", {
          file_id: fileId,
          submission_id: submissionId,
          field_usage: fieldUsage,
        }),
    }),
    setFileReuse: builder.mutation({
      invalidatesTags: ["IngestionData"],
      queryFn: async ({ fileId, reuse }) =>
        postAiAgents("Failed to set file reuse", "set-file-reuse", {
          file_id: fileId,
          reuse,
        }),
    }),
    rerunIngestionFile: builder.mutation({
      invalidatesTags: ["IngestionData", "IngestionFiles"],
      queryFn: async ({ fileName, fileUrl }) =>
        postAiAgents("Failed to rerun ingestion", "submission-ingestion", {
          files: [{ filename: fileName, file_url: fileUrl }],
          rerun: true,
        }),
    }),
    getIngestionFiles: builder.query({
      providesTags: ["IngestionFiles"],
      queryFn: async ({ unused_only = false }) =>
        postAiAgents("Failed to get ingestion files", "get-ingestion-files", {
          unused_only,
        }),
    }),
    getTriageResult: builder.query({
      // Tagged so a re-run, attach or detach refreshes an OPEN drawer. The pipeline list
      // polls every 10s, so a background re-triage used to clear the row's amber dot and
      // the drawer's stale pill from polled data while the drawer's report body — neither
      // polled nor invalidated — kept rendering the previous run. An outdated report was
      // presented as current, which is the one state the stale flag exists to prevent.
      providesTags: ["TriageResult"],
      queryFn: async (fileId) =>
        postAiAgents("Failed to get triage result", "get-triage-result", {
          file_id: fileId,
        }),
    }),
    getTriagePipeline: builder.query({
      providesTags: ["TriagePipeline"],
      queryFn: async () =>
        postAiAgents(
          "Failed to get triage pipeline",
          "get-triage-pipeline",
          {}
        ),
    }),
    archiveTriageResult: builder.mutation({
      invalidatesTags: ["TriagePipeline"],
      queryFn: async ({ fileId, folderId, archived }) => {
        const body = { archived };
        if (folderId) body.folder_id = folderId;
        else body.file_id = fileId;
        return postAiAgents(
          "Failed to archive triage result",
          "archive-triage-result",
          body
        );
      },
    }),
    markTriageReviewed: builder.mutation({
      invalidatesTags: ["TriagePipeline"],
      queryFn: async ({ fileId, folderId, reviewed }) => {
        const body = { reviewed };
        if (folderId) body.folder_id = folderId;
        else body.file_id = fileId;
        return postAiAgents(
          "Failed to mark triage reviewed",
          "mark-triage-reviewed",
          body
        );
      },
    }),
    saveTriageOverrides: builder.mutation({
      invalidatesTags: ["TriagePipeline"],
      queryFn: async ({ fileId, folderId, overrides, verdictOverride }) => {
        // Keys are snake_case because this request is parsed by a plain pydantic model,
        // not one of the camelizing ones — an unrecognised key is dropped with a 200.
        const body = { overrides };
        if (verdictOverride !== undefined)
          body.verdict_override = verdictOverride;
        if (folderId) body.folder_id = folderId;
        else body.file_id = fileId;
        return postAiAgents(
          "Failed to save triage overrides",
          "save-triage-overrides",
          body
        );
      },
    }),
    registerIngestionFile: builder.mutation({
      queryFn: async ({
        fileId,
        fileName,
        fileHash,
        fileSize,
        fileUrl,
        folderId,
        folderName,
        folderFileCount,
      }) => {
        return postAiAgents(
          "Failed to register ingestion file",
          "register-ingestion-file",
          {
            file_id: fileId,
            file_name: fileName,
            file_hash: fileHash,
            file_size: fileSize,
            file_url: fileUrl,
            ...(folderId && { folder_id: folderId }),
            ...(folderName && { folder_name: folderName }),
            ...(folderFileCount != null && {
              folder_file_count: folderFileCount,
            }),
          }
        );
      },
    }),
    runSubmissionTriage: builder.mutation({
      queryFn: async ({ files, ruleset }) =>
        postAiAgents("Triage failed", "submission-triage", { files, ruleset }),
    }),
    rerunSubmissionTriage: builder.mutation({
      invalidatesTags: ["TriagePipeline", "TriageResult"],
      queryFn: async ({ fileId, folderId, ruleset, mode, fileUrls }) => {
        // The server derives the file set from the registry, so the re-run
        // always covers every file currently attached to the item.
        const body = {};
        if (folderId) body.folder_id = folderId;
        else body.file_id = fileId;
        if (ruleset) body.ruleset = ruleset;
        // Omitted for the ordinary Re-run button so the deployment's configured mode
        // governs. The UI never sends one; it remains for API callers and tests.
        if (mode) body.mode = mode;
        // Fresh download URLs the caller minted for documents whose stored URL had
        // expired. The service cannot sign for the prefix they live under and has no
        // access to the files service, so it can only use a URL it is handed.
        if (fileUrls && Object.keys(fileUrls).length > 0)
          body.file_urls = fileUrls;
        // A failure naming unreadable documents is actionable (mint fresh URLs, retry),
        // so that one comes back as data instead of a thrown string.
        return postAiAgents(
          "Triage re-run failed",
          "rerun-submission-triage",
          body,
          {
            throwOnFailure: true,
            keepFailure: (data) => data.unreadable_file_ids?.length > 0,
          }
        );
      },
    }),
    attachIngestionFiles: builder.mutation({
      invalidatesTags: [
        "TriagePipeline",
        "IngestionFiles",
        "IngestionData",
        "TriageResult",
      ],
      queryFn: async ({ fileId, folderId, folderName, files }) => {
        const body = { files };
        if (folderId) body.folder_id = folderId;
        else body.file_id = fileId;
        if (folderName) body.folder_name = folderName;
        return postAiAgents(
          "Failed to attach files",
          "attach-ingestion-files",
          body,
          { throwOnFailure: true }
        );
      },
    }),
    // A ZIP member's id is minted by ai_agents, so the files service 404s on it. Returns
    // success:false for everything else, which the files service serves as before.
    ingestionFileUrl: builder.mutation({
      queryFn: async ({ fileId }) =>
        postAiAgents("Failed to get a download URL", "get-ingestion-file-url", {
          file_id: fileId,
        }),
    }),

    detachIngestionFiles: builder.mutation({
      invalidatesTags: [
        "TriagePipeline",
        "IngestionFiles",
        "IngestionData",
        "TriageResult",
      ],
      queryFn: async ({ folderId, fileIds }) =>
        postAiAgents(
          "Failed to remove files",
          "detach-ingestion-files",
          { folder_id: folderId, file_ids: fileIds },
          { throwOnFailure: true }
        ),
    }),
    generateExcelFromTables: builder.mutation({
      queryFn: async ({ tables, originalFileS3Key, addMetaPage = true }) => {
        const payload = {
          body: {
            tables,
            original_file_s3_key: originalFileS3Key,
            add_meta_page: addMetaPage,
          },
        };

        const response = await baseAiAgentsQuery({
          url: "generate-excel-from-tables",
          payload: payload,
        });

        if (response.error) {
          throw new Error(`Excel generation failed: ${response.error}`);
        }

        // Download the base64 encoded Excel file
        if (response.data?.excel_base64) {
          try {
            // Convert base64 to blob
            const byteCharacters = atob(response.data.excel_base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const FileSaver = require("file-saver");
            FileSaver.saveAs(blob, "extracted_tables.xlsx");
          } catch (e) {
            logger.exception(e);
          }
        }

        return {
          data: response.data,
        };
      },
    }),
  }),
});

// PDF Extraction Service
export const extractPdfData = async (fileData, options = {}) => {
  try {
    const authHeaders = await getAuthHeaders({
      "Content-Type": "application/json",
    });

    const requestBody = {
      file_url: fileData, // Can be data URL or regular URL
      generate_excel: options.generateExcel === true, // Default false
      generate_annotated_pdf: options.generatePdf !== false, // Default true
      clean_table: options.cleanTable || false,
      fill_threshold: options.fillThreshold || 0.3,
      remove_subtotal: options.removeSubtotal || false,
    };

    const response = await fetch(
      config.endpoints.aiAgents("extract-pdf-data"),
      {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`PDF extraction failed: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    logger.exception(error);
    throw error;
  }
};

// SOV Fixer Service
export class SovFixerService {
  /**
   * Submit SOV file for extraction
   * @param {string} fileUrl - S3 URL of the file to extract
   * @returns {Promise<{success: boolean, job_id: string, message: string}>}
   */
  static async submitExtraction(fileUrl) {
    const authHeaders = await getAuthHeaders({
      "Content-Type": "application/json",
    });

    const response = await fetch(
      config.endpoints.aiAgents("sov-fixer/extract"),
      {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          file_url: fileUrl,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`SOV extraction failed: ${response.status} ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Check status of SOV extraction job
   * @param {string} jobId - Job ID from submitExtraction
   * @returns {Promise<{success: boolean, job_id: string, status: string, complete: boolean, output_data?: object}>}
   */
  static async checkStatus(jobId) {
    const authHeaders = await getAuthHeaders({
      "Content-Type": "application/json",
    });

    const response = await fetch(
      config.endpoints.aiAgents("sov-fixer/status"),
      {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          job_id: jobId,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Status check failed: ${response.status} ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Get final extraction data for completed job
   * @param {string} jobId - Job ID from submitExtraction
   * @returns {Promise<{success: boolean, job_id: string, data: object}>}
   */
  static async getData(jobId) {
    const authHeaders = await getAuthHeaders({
      "Content-Type": "application/json",
    });

    const response = await fetch(config.endpoints.aiAgents("sov-fixer/data"), {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({
        job_id: jobId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get data: ${response.status} ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Poll for extraction completion with callbacks
   * @param {string} jobId - Job ID to poll
   * @param {object} callbacks - { onProgress, onComplete, onError }
   * @param {number} pollInterval - Polling interval in ms (default 3000)
   */
  static async pollExtraction(jobId, callbacks = {}, pollInterval = 3000) {
    try {
      while (true) {
        const statusResponse = await this.checkStatus(jobId);

        // Call progress callback with status
        if (callbacks.onProgress) {
          callbacks.onProgress({
            status: statusResponse.status,
            complete: statusResponse.complete,
            pct_complete: statusResponse.pct_complete,
          });
        }

        // Check if complete
        if (statusResponse.complete) {
          // Get final data
          const dataResponse = await this.getData(jobId);

          if (callbacks.onComplete) {
            callbacks.onComplete(dataResponse.data);
          }
          return dataResponse.data;
        }

        // Check if failed
        if (statusResponse.status === "FAILED") {
          throw new Error(statusResponse.error || "Extraction failed");
        }

        // Wait before polling again
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }
    } catch (error) {
      logger.exception(error);
      if (callbacks.onError) {
        callbacks.onError(error);
      }
      throw error;
    }
  }
}

export const {
  /** NEW AI Agents column mapping suggestions
   * Same input/output format as useGetColumnMappingSuggestionsQuery but uses
   * the new AI agents service when feature flag is enabled.
   *
   * Feature Flag: Set "use-ai-agents-column-mapping" to true in localStorage
   * MMM.featureFlags to use the new AI agents service. Falls back to legacy
   * service on error.
   *
   * Returns additional "source" field indicating which service was used.
   */
  useGetColumnMappingSuggestionsAIQuery,
  useGetSheetAnalysisQuery,
  useMatchClaimsMutation: useMatchClaims,
  useDetectDuplicatesMutation,
  useGetAvailableIngestionDataQuery,
  useMarkFileAsUsedMutation,
  useSetFileReuseMutation,
  useRerunIngestionFileMutation,
  useRegisterIngestionFileMutation,
  useGetIngestionFilesQuery,
  useGetTriageResultQuery,
  useGetTriagePipelineQuery,
  useArchiveTriageResultMutation,
  useMarkTriageReviewedMutation,
  useSaveTriageOverridesMutation,
  useGenerateExcelFromTablesMutation,
  useRunSubmissionTriageMutation,
  useRerunSubmissionTriageMutation,
  useAttachIngestionFilesMutation,
  useDetachIngestionFilesMutation,
  useIngestionFileUrlMutation,
  useAgentSearchIngestionFieldsMutation,
} = aiAgentsApi;
